import { AttackPattern, PatternState } from '../AttackPattern.js';
import { Ostinato } from './Ostinato.js';
import { Understudy } from './Understudy.js';

/**
 * Everyone At Once — The Anthem's phase-2 form (Act III-6, ACT3-BIBLE §3).
 *
 * The Chorus recapitulates Act III the way `TheUnmaking` and `Unsinging` did
 * their own acts, but the recapitulation has a shape of its own: not one
 * texture after another, but all of them at reduced individual intensity, the
 * *composite* being the threat. An anthem is a lot of people singing at once.
 *
 * The two textures that are patterns (the replayed path, the mirrored build)
 * are driven here as children rather than reimplemented — a child pattern is
 * just an object with the same lifecycle, so this owns them, begins them, and
 * restarts each one the moment it finishes so both run continuously for the
 * whole window. The other two Act III textures (the tithe ring, the second
 * body) are fight-long systems already live on the Anthem's def by this phase,
 * so they need nothing from here; they are simply also happening.
 */
export class EveryoneAtOnce extends AttackPattern {
  constructor({ config }) {
    super({ name: 'Everyone At Once', telegraphDuration: 1.2, activeDuration: 8.0, minPhase: 2 });
    this.config = config;
    // Injected by SpawningSystem for the Understudy child, which needs the
    // player's live loadout (see its own comment).
    this.combat = null;
    this._children = [];
  }

  onBegin(boss, player) {
    this._children = [
      new Ostinato({ minPhase: 0, config: this.config.ostinato }),
      new Understudy({ minPhase: 0, config: this.config.understudy }),
    ];
    for (const child of this._children) {
      if ('combat' in child) child.combat = this.combat;
      child.begin(boss, player);
    }
    this._ringTimer = 0;
  }

  onActiveTick(dt, boss, player, pool) {
    for (const child of this._children) {
      // The children's own names label their bullets in the death recap, which
      // is right: "Understudy" is a truer answer to "what killed me" than
      // "Everyone At Once" would be.
      pool.sourceLabel = child.name;
      child.update(dt, boss, player, pool);
      if (child.isDone) child.begin(boss, player);
    }
    pool.sourceLabel = this.name;

    // The anthem's own voice under all of it — a slow, wide ring that never
    // stops, so there is always something to dodge that isn't yours.
    this._ringTimer -= dt;
    if (this._ringTimer <= 0) {
      this._ringTimer = this.config.ringInterval;
      for (let i = 0; i < this.config.ringCount; i++) {
        const angle = (i / this.config.ringCount) * Math.PI * 2 + Math.random() * 0.1;
        pool.fire({
          x: boss.x,
          y: boss.y,
          vx: Math.cos(angle) * this.config.ringSpeed,
          vy: Math.sin(angle) * this.config.ringSpeed,
          radius: 3,
          damage: this.config.ringDamage,
          color: this.config.color,
          lifetime: 4,
        });
      }
    }
  }

  onEnd() {
    this._children = [];
  }

  getVisual(boss) {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    const visuals = this._children.map((child) => child.getVisual(boss)).filter(Boolean);
    return { kind: 'composite', children: visuals };
  }
}
